import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  TextField,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import api from '../../services/api';
import { ALL_NAV_ITEMS, getNavPath } from './layout/navConfig';

// Ticket 2.4 — global command palette (⌘K / Ctrl+K).
//
// Client-side over data already available to the therapist: the 13 nav routes
// plus the assigned caseload (fetched once on first open). Selecting a client
// navigates to ?tab=clients&clientId=<id>, which TherapistDashboard resolves
// into the client detail view (the header hydrates the name — ticket 1.4).
// No new backend endpoint and no new dependency (native keydown listener).

const MAX_CLIENT_RESULTS = 8;

const CommandPalette = ({ token }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [loadedClients, setLoadedClients] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);

  // Global ⌘K / Ctrl+K to toggle the palette.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Lazy-load the caseload the first time the palette opens.
  useEffect(() => {
    if (open && !loadedClients && token) {
      api.getAssignedClients(token)
        .then((list) => { setClients(list || []); setLoadedClients(true); })
        .catch(() => setLoadedClients(true)); // routes still work without the caseload
    }
  }, [open, loadedClients, token]);

  // Reset transient state whenever the palette opens.
  useEffect(() => {
    if (open) { setQuery(''); setActiveIndex(0); }
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const navMatches = ALL_NAV_ITEMS
      .filter((item) => !q || item.label.toLowerCase().includes(q))
      .map((item) => ({ type: 'route', id: `route:${item.id}`, label: item.label, icon: item.icon, to: getNavPath(item) }));

    const clientMatches = (!q ? [] : clients
      .filter((c) => c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q))
      .slice(0, MAX_CLIENT_RESULTS)
      .map((c) => ({
        type: 'client',
        id: `client:${c._id}`,
        label: c.name || c.email || 'Client',
        secondary: c.email,
        to: `/therapist/dashboard?tab=clients&clientId=${c._id}`,
      })));

    return [...clientMatches, ...navMatches];
  }, [query, clients]);

  // Keep the highlighted row in range as results change.
  useEffect(() => { setActiveIndex(0); }, [query]);

  const select = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    navigate(item.to);
  }, [navigate]);

  const onInputKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      select(results[activeIndex]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="sm"
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
      sx={{ '& .MuiDialog-paper': { position: 'absolute', top: 80, m: 0, borderRadius: 2 } }}
    >
      <Box sx={{ p: 1.5, borderBottom: '1px solid #E5E7EB' }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          variant="standard"
          placeholder="Search clients or jump to a page…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKeyDown}
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <List dense sx={{ maxHeight: 360, overflowY: 'auto', py: 0 }}>
        {results.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No matches</Typography>
          </Box>
        ) : (
          results.map((item, i) => (
            <ListItemButton
              key={item.id}
              selected={i === activeIndex}
              onMouseEnter={() => setActiveIndex(i)}
              onClick={() => select(item)}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {item.type === 'client' ? <PersonIcon fontSize="small" /> : item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                secondary={item.type === 'client' ? item.secondary : 'Go to page'}
              />
            </ListItemButton>
          ))
        )}
      </List>
      <Box sx={{ px: 2, py: 1, borderTop: '1px solid #E5E7EB', display: 'flex', gap: 2 }}>
        <Typography variant="caption" color="text.secondary">↑↓ navigate</Typography>
        <Typography variant="caption" color="text.secondary">↵ open</Typography>
        <Typography variant="caption" color="text.secondary">esc close</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>⌘K</Typography>
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
